import type { ExperimentPipeline, PipelineStage } from 'types';
import { Layers } from 'lucide-react';
import { cn } from 'helpers/cn';
import { ADVANCE_RULE_HINTS, ADVANCE_RULE_LABELS } from './labels';
import { CreatePipelineForm } from './CreatePipelineForm';

/**
 * First-run screen for the pipelines index.
 *
 * Same standard as the survey library's SurveysEmptyState: the old empty panel said
 * what a pipeline was in one paragraph, but a first-time reader also has to know how
 * a board decides to move on and that nothing runs until they arm it — the two facts
 * that decide whether the feature is safe to try. It replaces the index toolbar,
 * because the one control worth showing is the one that ends this state.
 */

/** The life of a board, in the order someone will actually walk it. */
const STEPS = [
  'Drag draft experiments into stages. Only drafts can join a board — anything already running or finished is refused.',
  'Order the stages. Experiments that would collide on the same page go in different stages, each at full traffic, rather than being made mutually exclusive and splitting the traffic.',
  'Leave it as a dry run first: the engine records what it would have started and stopped, and touches nothing.',
  'When the plan reads right, flip "Run automatically" — and, if you want the winner shipped without you, "Deploy the winner".',
];

const ADVANCE_RULES = Object.keys(ADVANCE_RULE_LABELS) as PipelineStage['advanceRule'][];

type PipelinesEmptyStateProps = {
  createPipeline: (name: string) => Promise<ExperimentPipeline | null>;
  onOpen: (pipelineId: number) => void;
};

export const PipelinesEmptyState = ({ createPipeline, onOpen }: PipelinesEmptyStateProps) => (
  <div className="flex flex-col gap-[14px]">
    <section className="bg-panel border-line rounded-panel border px-4 py-10 sm:px-6 sm:py-12" data-first-run>
      <div className="mx-auto max-w-2xl text-center">
        <Layers className="text-dim mx-auto size-10" strokeWidth={1.25} />
        {/* h1: the screen hides its page header on first-run, so this is the page's
            only heading — same level as the surveys and implementation explainers. */}
        <h1 className="text-foreground m-0 mt-4 text-title font-medium tracking-tight">No pipelines yet</h1>
        <p className="supporting mx-auto mt-2 max-w-xl">
          A pipeline is an ordered run of stages, where a stage is a set of experiments that run at the same time.
          Stages run one after another, so a season of testing becomes one board: what runs now, what runs next, and
          what already finished.
        </p>

        <CreatePipelineForm createPipeline={createPipeline} onCreated={onOpen} className="mt-5 justify-center" />
      </div>
    </section>

    <section className="bg-panel border-line rounded-panel border">
      <div className="border-line flex items-center justify-between border-b px-3 py-2.5">
        <span className="micro-label">When a stage moves on</span>
        <span className="micro-label tracking-normal">{ADVANCE_RULES.length}</span>
      </div>
      {/*
        Same labels and hints the board's stage editor uses, from labels.ts — the rule
        is explained once, so this screen and the editor cannot describe the same
        three behaviours differently.
      */}
      <div className="grid gap-2.5 p-3 sm:grid-cols-3">
        {ADVANCE_RULES.map((rule) => (
          <div key={rule} className="border-line bg-panel2 rounded-card border p-3.5">
            <p className="text-foreground m-0 text-body font-medium">{ADVANCE_RULE_LABELS[rule]}</p>
            <p className="supporting m-0 mt-2">{ADVANCE_RULE_HINTS[rule]}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="bg-panel border-line rounded-panel border">
      <div className="border-line border-b px-3 py-2.5">
        <span className="micro-label">From plan to automation</span>
      </div>
      <ol className="m-0 flex list-none flex-col gap-2.5 p-3">
        {STEPS.map((step, index) => (
          <li key={step} className="flex items-start gap-2.5">
            <span
              className={cn(
                'border-line text-dim num flex size-5 shrink-0 items-center justify-center',
                'rounded-full border text-nano',
              )}
              aria-hidden="true"
            >
              {index + 1}
            </span>
            <p className="supporting m-0">{step}</p>
          </li>
        ))}
      </ol>
      <div className="border-line border-t px-3 py-2.5">
        {/* The fact that makes the feature safe to try. Same promise the automation
            panel makes: "Off records what the engine would have done." */}
        <p className="supporting m-0">
          A new pipeline starts as a dry run. Nothing is started, stopped or deployed until you arm it.
        </p>
      </div>
    </section>
  </div>
);
